import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearch } from '@tanstack/react-router'
import { AppIcon } from './ui/icon'
import { mdiCheck, mdiClose, mdiEyeOffOutline, mdiEyeOutline, mdiPencil, mdiTextBoxSearchOutline, mdiUndoVariant } from '@mdi/js'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Alert, AlertDescription } from './ui/alert'
import { NullableDateField } from './ui/NullableDateField'
import { PropertySourceBadge } from './PropertySourceBadge'
import { Can, useCan } from '../contexts/PermissionsContext'
import { useLocale } from '../contexts/LocaleContext'
import { useFocusAndPulseRow } from '../hooks/useFocusAndPulseRow'
import {
  useContractType,
  useContractTypes,
  useContractTypeProperties,
  useContractProperties,
  useExtractionReviewStats,
  useSetContractPropertyValue,
  useDeleteContractPropertyValue,
  useReextractContractClauses,
  usePropertyEnumValues,
  type ContractAsset,
  type ContractTypeProperty,
  type ContractPropertyValue,
  type ExtractionReliability,
  type ExtractionReviewStat,
  type PropertyDefinition,
} from '../hooks/useQueries'
import {
  ClauseReliabilityBadge,
  STATE_VARIANT,
  clauseChecks,
  extractionState,
  useCheckReason,
} from './contracts/ClauseReliability'
import { EmptyState } from './ui/empty-state'
import { mdiFormatListBulletedType } from '@mdi/js'
import { useJobPoll, jobErrorMessage } from './plan-editor/useJobPoll'
import { apiErrorDetail } from '../lib/apiError'
import { assetsBlockFamily, isHiddenProperty } from '../lib/contract-clauses'
import { ContractAssetsClause } from './contracts/ContractAssetsClause'

// Clause families (display_group letters). Rendered in this order; localized
// headings live under clauses.family.* in the contracts namespace.
// I is skipped: it is the value schema (columns on contract_data), not a family.
const FAMILY_ORDER = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L']

// Duration units and amount tax bases exposed by the compact v1 editors.
const DURATION_UNITS = ['DAY', 'WEEK', 'MONTH', 'YEAR']
const TAX_BASES = ['excluding_tax', 'including_tax', 'not_applicable']

// Badge tone per approval state (spec 2026-07-17-14 §2's confidence/status
// chip, re-based on approved_at by spec 2026-07-29-04) — same success/warning
// semantic vocabulary as the rest of the app's status pills, never hardcoded
// colors.
const APPROVAL_VARIANT: Record<string, 'success' | 'warning'> = {
  approved: 'success',
  to_review: 'warning',
}

// Sources whose values are machine-produced. They are the only ones that
// carry a review state — a human typing a value has already "reviewed" it —
// so this set gates the approval chip, NOT the source badge. The badge shows
// for every source there is, manual included: the right-hand column answers
// "where does this value come from?", and "a person set it" is an answer
// (user, 2026-09-16). PropertySourceBadge already renders the manual case,
// naming the user who wrote it.
const MACHINE_SOURCES = new Set(['document_extraction', 'import', 'derivation'])

// Label flexes to fill the leftover space; value and source are fixed-width
// columns pinned together on the right, shared by every clause row (date and
// generic alike) in a family card. Because the template is identical (and
// not content-driven) across sibling rows, the value column and the source
// badges line up into straight columns instead of each row's value text
// pushing its own badges around — and because value+source are fixed widths
// rather than "whatever's left", they sit right next to each other at the
// row's right edge instead of floating apart with a big gap between them.
// The value/source columns narrow below `sm` so the label (via minmax's 1fr)
// still gets enough room on a phone-width card instead of wrapping to four lines.
const ROW_GRID = 'grid grid-cols-[minmax(6rem,1fr)_8rem_8rem] sm:grid-cols-[1fr_11rem_11rem] gap-x-2 sm:gap-x-3 gap-y-1 items-start py-3'

interface ContractClausesTabProps {
  org: string
  contractUid: string
  contractTypeUid: string
  /** Closed minutes: every clause is display only. */
  readOnly?: boolean
  /**
   * The document this contract was read from. Undefined for a contract created
   * by hand or by import — the re-read action is hidden then, because there is
   * nothing to put back in front of the extractor.
   */
  sourceDocumentUid?: string
  /**
   * The contract's assets. Leases, insurances and maintenance contracts list
   * them, read only, where their clauses once asked for them as a property.
   */
  assets?: ContractAsset[]
}

// Resolve a multilingual name object to the active locale (locale → en → fr → first).
function localized(names: Record<string, string> | undefined, locale: string, fallback = ''): string {
  if (!names) return fallback
  return names[locale] || names.en || names.fr || Object.values(names)[0] || fallback
}

export function ContractClausesTab({ org, contractUid, contractTypeUid, sourceDocumentUid, readOnly, assets = [] }: ContractClausesTabProps) {
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()
  const reextract = useReextractContractClauses(org, contractUid)
  // Tracks the doc_entity_extraction run past the POST that only enqueues it
  // (#1380): a fixed 10s poll of the job (useJobPoll, the same mechanism
  // digitization/calibration use) is what tells the user it finished, and
  // surfaces the failure reason instead of leaving a stale "queued" message
  // up regardless of outcome.
  const [reextractJobUid, setReextractJobUid] = useState<string | null>(null)
  const [reextractState, setReextractState] = useState<'idle' | 'running' | 'success' | 'failed'>('idle')
  const [reextractError, setReextractError] = useState<string | null>(null)
  const reextractPoll = useJobPoll(org, reextractJobUid)

  // "Back to its clause" side of a document highlight click (user,
  // 2026-09-16): `?focusClause=<code>` scrolls that row into view and pulses
  // it. The contract route has no validateSearch, so the raw param comes
  // through unstripped. See useFocusAndPulseRow and each row's existing
  // `clause-${def.code}` testid below.
  const search = useSearch({ strict: false }) as { focusClause?: string }
  useFocusAndPulseRow(search.focusClause ? `clause-${search.focusClause}` : undefined)

  const {
    data: typeProps = [],
    isLoading: loadingDefs,
    isError: defsFailed,
    refetch: refetchDefs,
  } = useContractTypeProperties(org, contractTypeUid, {
    enabled: !!contractTypeUid,
  })
  const {
    data: values = [],
    isLoading: loadingValues,
    isError: valuesFailed,
    refetch: refetchValues,
  } = useContractProperties(org, contractUid)
  // The organization's own reviews of this exact type's extracted clauses,
  // shown next to the reference measure (spec 2026-10-27-12 §E).
  const { data: contractType } = useContractType(org, contractTypeUid)
  const { data: contractTypes = [] } = useContractTypes(org)
  const assetsFamily = useMemo(
    () => assetsBlockFamily(contractTypes as any, contractTypeUid),
    [contractTypes, contractTypeUid],
  )
  // Properties flagged `hidden` (a uid list, a vague label whose real link is
  // a relation) stay out of sight until the person reveals them, greyed.
  const [showHidden, setShowHidden] = useState(false)
  const canReadContracts = useCan('contracts.read')
  const { data: reviewStats } = useExtractionReviewStats(org, contractType?.code, { enabled: canReadContracts })
  const reviewsByCode = useMemo(() => {
    const map = new Map<string, ExtractionReviewStat>()
    for (const r of reviewStats) map.set(r.property_code, r)
    return map
  }, [reviewStats])

  // Current values keyed by property-definition uid for O(1) lookup per field.
  const valuesByDefUid = useMemo(() => {
    const map = new Map<string, ContractPropertyValue>()
    for (const v of values) {
      const uid = (v.property_definition as PropertyDefinition | undefined)?.uid
      if (uid) map.set(uid, v)
    }
    return map
  }, [values])

  // Only v1 manual-entry clauses, grouped by family and ordered. Hidden ones
  // are counted, and kept only when revealed.
  const hiddenCount = useMemo(
    () => typeProps.filter(p => p.flags?.includes('manual_entry_v1') && isHiddenProperty(p.property_definition as PropertyDefinition | undefined)).length,
    [typeProps],
  )
  const groups = useMemo(() => {
    const v1 = typeProps.filter(p => p.flags?.includes('manual_entry_v1') && p.property_definition
      && (showHidden || !isHiddenProperty(p.property_definition as PropertyDefinition)))
    const byFamily = new Map<string, ContractTypeProperty[]>()
    for (const p of v1) {
      const family = (p.display_group || '?').toUpperCase()
      const list = byFamily.get(family) || []
      list.push(p)
      byFamily.set(family, list)
    }
    // The assets block has a home even when its family has no clause left.
    if (assetsFamily && !byFamily.has(assetsFamily)) byFamily.set(assetsFamily, [])
    for (const list of byFamily.values()) {
      list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    }
    // Known families first (A..H), then any unexpected family codes alphabetically.
    const families = Array.from(byFamily.keys()).sort((a, b) => {
      const ia = FAMILY_ORDER.indexOf(a)
      const ib = FAMILY_ORDER.indexOf(b)
      if (ia === -1 && ib === -1) return a.localeCompare(b)
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })
    return families.map(family => ({ family, props: byFamily.get(family)! }))
  }, [typeProps, showHidden, assetsFamily])

  // The values to verify first: unreviewed extractions of a low or unmeasured
  // field, and every value failing a consistency check. The import's draft
  // lease list counts the same way (GET /leases?include=values_to_verify).
  const toVerifyCount = useMemo(() => {
    let n = 0
    for (const { props } of groups) {
      for (const p of props) {
        if (isHiddenProperty(p.property_definition as PropertyDefinition)) continue
        const value = valuesByDefUid.get((p.property_definition as PropertyDefinition).uid)
        if (extractionState(value, p.extraction_reliability) === 'to_verify') n++
      }
    }
    return n
  }, [groups, valuesByDefUid])

  // The poll settles onto a final status exactly once per run: refresh the
  // clause values on success (the job rewrote them), capture the failure
  // reason otherwise, and release the job uid so the poll disables itself.
  useEffect(() => {
    if (!reextractJobUid || !reextractPoll.finalStatus) return
    if (reextractPoll.finalStatus === 'success') {
      setReextractState('success')
      void refetchValues()
    } else {
      setReextractState('failed')
      setReextractError(jobErrorMessage(reextractPoll.execution?.error))
    }
    setReextractJobUid(null)
  }, [reextractPoll.finalStatus, reextractPoll.execution, reextractJobUid, refetchValues])

  if (!contractTypeUid) {
    return (
      <EmptyState
        variant="inline"
        icon={mdiFormatListBulletedType}
        title={t('clauses.no_type')}
        data-testid="clauses-no-type"
      />
    )
  }

  if (loadingDefs || loadingValues) {
    return (
      <div className="flex items-center justify-center h-40" data-testid="clauses-loading">
        <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // A failed request must never read as "this type has no clauses": the empty
  // state below is a statement about the ONTOLOGY, and rendering it when the
  // API is simply unreachable sends the reader hunting for a missing type
  // configuration that was never missing. Checked before the empty state,
  // since a failed query also yields zero groups.
  if (defsFailed || valuesFailed) {
    return (
      <div className="flex flex-col items-start gap-3 p-6" data-testid="clauses-error">
        <p className="text-sm text-destructive">{t('clauses.error')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refetchDefs()
            void refetchValues()
          }}
          data-testid="clauses-retry"
        >
          {t('clauses.retry')}
        </Button>
      </div>
    )
  }

  if (groups.length === 0 && hiddenCount === 0) {
    return (
      <EmptyState
        variant="inline"
        icon={mdiFormatListBulletedType}
        title={t('clauses.empty')}
        data-testid="clauses-empty"
      />
    )
  }

  return (
    <div className="space-y-6" data-testid="clauses-content">
      <p
        className={`text-sm ${toVerifyCount > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
        data-testid="clauses-to-verify-count"
        data-count={toVerifyCount}
      >
        {t('clauses.to_verify_count', { count: toVerifyCount })}
      </p>
      {sourceDocumentUid && (
        <Can perm="contracts.write">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">{t('clauses.reextract_hint')}</p>
            <Button
              variant="outline"
              size="sm"
              disabled={reextract.isPending || reextractState === 'running'}
              aria-label={t('clauses.reextract')}
              title={t('clauses.reextract')}
              onClick={() => {
                setReextractState('running')
                setReextractError(null)
                reextract.mutate({
                  onSuccess: (data: { job_uid?: string }) => setReextractJobUid(data?.job_uid ?? null),
                  onError: () => setReextractState('idle'),
                })
              }}
              data-testid="clauses-reextract-btn"
            >
              <AppIcon path={mdiTextBoxSearchOutline} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{t('clauses.reextract')}</span>
            </Button>
          </div>
        </Can>
      )}

      {reextractState === 'running' && (
        <Alert data-testid="clauses-reextract-queued">
          <AlertDescription>{t('clauses.reextract_queued')}</AlertDescription>
        </Alert>
      )}

      {reextractState === 'success' && (
        <Alert data-testid="clauses-reextract-success">
          <AlertDescription>{t('clauses.reextract_success')}</AlertDescription>
        </Alert>
      )}

      {reextractState === 'failed' && (
        <Alert variant="destructive" data-testid="clauses-reextract-job-failed">
          <AlertDescription>{t('clauses.reextract_job_failed', { reason: reextractError })}</AlertDescription>
        </Alert>
      )}

      {reextract.isError && (
        <Alert variant="destructive" data-testid="clauses-reextract-error">
          <AlertDescription>{t('clauses.reextract_error')}</AlertDescription>
        </Alert>
      )}

      {hiddenCount > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHidden(v => !v)}
            aria-pressed={showHidden}
            data-testid="clauses-show-hidden-toggle"
          >
            <AppIcon path={showHidden ? mdiEyeOffOutline : mdiEyeOutline} size="sm" className="mr-1" />
            {showHidden ? t('clauses.hide_hidden') : t('clauses.show_hidden', { count: hiddenCount })}
          </Button>
        </div>
      )}

      {groups.map(({ family, props }) => (
        <Card key={family} data-testid={`clauses-family-${family}`}>
          <CardHeader>
            <CardTitle className="text-base">
              <span className="text-muted-foreground mr-2">{family}.</span>
              {t(`clauses.family.${family}` as any, { defaultValue: family })}
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            {family === assetsFamily && <ContractAssetsClause org={org} assets={assets} rowClassName={ROW_GRID} />}
            {props.map(p => isHiddenProperty(p.property_definition as PropertyDefinition) ? (
              <div
                key={(p.property_definition as PropertyDefinition).uid}
                className="opacity-60"
                data-hidden="true"
                title={t('clauses.hidden_hint')}
                data-testid={`clause-hidden-${(p.property_definition as PropertyDefinition).code}`}
              >
                <ContractClauseField
                  org={org}
                  contractUid={contractUid}
                  def={p.property_definition as PropertyDefinition}
                  current={valuesByDefUid.get((p.property_definition as PropertyDefinition).uid)}
                  locale={locale}
                  readOnly={readOnly}
                  reliability={p.extraction_reliability}
                  review={reviewsByCode.get((p.property_definition as PropertyDefinition).code)}
                />
              </div>
            ) : (
              <ContractClauseField
                key={(p.property_definition as PropertyDefinition).uid}
                org={org}
                contractUid={contractUid}
                def={p.property_definition as PropertyDefinition}
                current={valuesByDefUid.get((p.property_definition as PropertyDefinition).uid)}
                locale={locale}
                readOnly={readOnly}
                reliability={p.extraction_reliability}
                review={reviewsByCode.get((p.property_definition as PropertyDefinition).code)}
              />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// One clause field: click-to-edit, per data_type editor. Reuses the asset/doc
// inline-edit idiom (display → editor with check/cancel, Enter saves, Esc cancels).
// ---------------------------------------------------------------------------

type JsonSubtype = 'duration' | 'amount' | 'generic'

function jsonSubtype(def: PropertyDefinition, current?: ContractPropertyValue): JsonSubtype {
  const declared = (def.properties as any)?.value_type || (def.properties as any)?.json_type
  if (declared === 'duration' || declared === 'amount') return declared
  const v = current?.value as any
  if (v && typeof v === 'object') {
    if ('amount' in v || 'currency' in v) return 'amount'
    if ('unit' in v) return 'duration'
  }
  return 'generic'
}

export interface ClauseValueControlProps {
  org: string
  contractUid: string
  def: PropertyDefinition
  current?: ContractPropertyValue
  locale: string
  // The Clauses tab places this as a direct grid item (ROW_GRID: label /
  // value / source) — letting the editor span into the adjacent source
  // column gives duration/amount editors (input + unit select + two buttons)
  // enough room. Other embeddings (e.g. the Improvement tab's plain card,
  // not a grid) leave this off.
  spanEditingColumns?: boolean
  // The contract no longer accepts writes (closed minutes): display only.
  readOnly?: boolean
}

// The interactive value itself — display (click to edit) or editor, per
// data_type — with no label and no provenance badge, so it can be dropped
// into any layout that already has its own place for those (the Clauses
// tab's row grid, the Improvement tab's "what the contract says" card).
export function ClauseValueControl({ org, contractUid, def, current, locale, spanEditingColumns, readOnly }: ClauseValueControlProps) {
  const { t } = useTranslation('contracts')
  const setMutation = useSetContractPropertyValue(org, contractUid)
  // Multivalued clauses are marked by the "multiple" flag on the property
  // definition itself (the signal travels with the embedded property_definition).
  const isMultiple = !!def.flags?.includes('multiple')
  const subtype = jsonSubtype(def, current)

  // Enum values only fetched for enum-typed clauses.
  const { data: enumValues = [] } = usePropertyEnumValues(org, def.uid, {
    enabled: def.data_type === 'enum',
  })

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<any>(current?.value ?? null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (!editing) setDraft(current?.value ?? null)
  }, [current, editing])

  const testId = `clause-${def.code}`

  // A refused value (validation error) keeps the editor open and says why.
  const save = async (value: unknown) => {
    setSaving(true)
    setSaveError(null)
    try {
      await setMutation.mutateAsync(def.uid, value)
      setEditing(false)
    } catch (err) {
      setSaveError(apiErrorDetail(err, t('clauses.save_error')))
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setDraft(current?.value ?? null)
    setSaveError(null)
    setEditing(false)
  }

  // Date reuses the dedicated inline date field (self-contained edit state).
  if (def.data_type === 'date') {
    return (
      <div className="min-w-0">
        <NullableDateField
          value={(current?.value as string) ?? null}
          onSave={async (next) => { await setMutation.mutateAsync(def.uid, next) }}
          testIdPrefix={testId}
        />
      </div>
    )
  }

  const displayValue = renderDisplay({ def, current, enumValues, locale, subtype, isMultiple, t })

  if (editing) {
    return (
      <div className={`min-w-0 flex flex-wrap items-center gap-1 ${spanEditingColumns ? 'col-span-2' : ''}`}>
        <ClauseEditor
          def={def}
          draft={draft}
          setDraft={setDraft}
          enumValues={enumValues}
          locale={locale}
          subtype={subtype}
          isMultiple={isMultiple}
          onEnter={() => save(normalizeDraft(draft))}
          testId={testId}
        />
        <Button
          onClick={() => save(normalizeDraft(draft))}
          disabled={saving}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
          data-testid={`save-${testId}`}
          aria-label={t('clauses.save')}
          title={t('clauses.save')}
        >
          {saving ? '...' : <AppIcon path={mdiCheck} size="md" />}
        </Button>
        <Button
          onClick={cancel}
          disabled={saving}
          variant="outline"
          className="px-2 py-1 text-sm"
          data-testid={`cancel-${testId}`}
          aria-label={t('clauses.cancel')}
          title={t('clauses.cancel')}
        >
          <AppIcon path={mdiClose} size="md" />
        </Button>
        {saveError && (
          <p className="basis-full text-sm text-destructive" data-testid={`error-${testId}`}>{saveError}</p>
        )}
      </div>
    )
  }

  // A system-managed clause is written by the platform (a vote, a sitting,
  // an action), never typed: the generic API answers 403.
  if (readOnly || def.flags?.includes('system_managed')) {
    return (
      <div className="min-w-0 py-1.5 text-sm text-foreground" data-testid={`field-${testId}`} data-readonly="true">
        {displayValue ?? <span className="text-muted-foreground">{t('clauses.empty_value')}</span>}
      </div>
    )
  }

  return (
    <div className="min-w-0">
      <button
        type="button"
        className="group inline-flex min-w-0 max-w-full items-center gap-1 -ml-2 text-left text-sm text-foreground cursor-pointer hover:bg-muted/50 px-2 py-1.5 rounded transition-colors"
        onClick={() => setEditing(true)}
        data-testid={`field-${testId}`}
      >
        <span>
          {displayValue ?? <span className="text-muted-foreground">{t('clauses.empty_value')}</span>}
        </span>
        <AppIcon path={mdiPencil} size="xs" className="opacity-0 group-hover:opacity-50 shrink-0" />
      </button>
    </div>
  )
}

interface ContractClauseFieldProps {
  org: string
  contractUid: string
  def: PropertyDefinition
  current?: ContractPropertyValue
  locale: string
  readOnly?: boolean
  /** The measured reliability of this clause when it is extractable. */
  reliability?: ExtractionReliability | null
  /** The organization's own reviews of this clause. */
  review?: ExtractionReviewStat
}

function ContractClauseField({ org, contractUid, def, current, locale, readOnly, reliability, review }: ContractClauseFieldProps) {
  const { t } = useTranslation('contracts')
  const checkReason = useCheckReason()
  const label = localized(def.name, locale, def.code)
  const testId = `clause-${def.code}`

  // Provenance row (spec 2026-07-17-14 §2, re-based on the canonical
  // provenance pair by spec 2026-07-29-04): the source badge renders for ANY
  // recorded source, manual included — the column exists to say where a value
  // came from, and a value a person typed has a source too. The approval chip
  // stays machine-only: `approved_at` is a review of what a machine proposed,
  // and pinning "to review" on a value a human just entered would be asking
  // them to double-check their own typing.
  const clauseSource = (current?.source_details as { source?: string } | undefined)?.source
  const isMachineSourced = !!clauseSource && MACHINE_SOURCES.has(clauseSource)
  // An extraction's chip takes its tone from the field's measured tier; a
  // failed check reads « à vérifier » with its reason, whatever the source
  // (spec 2026-10-27-12 §D, §E).
  const state = extractionState(current, reliability)
  const checks = clauseChecks(current)
  // An amendment shows the clauses of the contract it amends unless it
  // overrides them (spec 2026-11-03-05): an inherited value says where it
  // comes from, an override can be reverted to the inherited value.
  const inheritedFrom = current?.inherited_from
  const overrides = current?.overrides
  const canWrite = !readOnly && !def.flags?.includes('system_managed')
  const inheritedBadge = inheritedFrom ? (
    <Link
      to="/$org/contracts/$contractUid"
      params={{ org, contractUid: inheritedFrom.uid }}
      data-testid={`clause-inherited-${def.code}`}
      title={t('clauses.inherited_hint')}
    >
      <Badge variant="secondary" className="text-[10px] font-normal">
        {t('clauses.inherited_from', { name: inheritedFrom.name })}
      </Badge>
    </Link>
  ) : null
  const revertButton = overrides && canWrite ? (
    <RevertToInheritedButton org={org} contractUid={contractUid} def={def} from={overrides.name} />
  ) : null
  const provenanceRow = clauseSource ? (
    <div className="flex flex-wrap items-center gap-2" data-testid={`clause-provenance-${testId}`}>
      {inheritedBadge}
      {/* A manual write stamps approved_by with its author, so the approver
          of a manual clause is the person who set it (#1616). */}
      <PropertySourceBadge
        org={org}
        sourceDetails={current!.source_details!}
        updatedByName={clauseSource === 'manual' ? current!.approved_by_name : undefined}
        updatedAt={current!.updated_at}
        origin={{ kind: 'contract', contractUid, clauseCode: def.code }}
      />
      {isMachineSourced && clauseSource === 'document_extraction' && state && (
        <Badge
          variant={STATE_VARIANT[state]}
          className="text-[10px] font-normal"
          data-testid={`clause-approval-${testId}`}
          data-state={state}
        >
          {t(`clauses.state.${state}` as any)}
        </Badge>
      )}
      {isMachineSourced && clauseSource !== 'document_extraction' && (
        <Badge
          variant={APPROVAL_VARIANT[current!.approved_at ? 'approved' : 'to_review']}
          className="text-[10px] font-normal"
          data-testid={`clause-approval-${testId}`}
        >
          {t(`clauses.approval.${current!.approved_at ? 'approved' : 'to_review'}` as any)}
        </Badge>
      )}
      {revertButton}
    </div>
  ) : (inheritedBadge || revertButton) ? (
    <div className="flex flex-wrap items-center gap-2" data-testid={`clause-provenance-${testId}`}>
      {inheritedBadge}
      {revertButton}
    </div>
  ) : null

  return (
    <div className={ROW_GRID} data-testid={testId}>
      <span className="text-sm font-medium text-foreground pt-1.5">{label}</span>
      <ClauseValueControl org={org} contractUid={contractUid} def={def} current={current} locale={locale} spanEditingColumns readOnly={readOnly} />
      <div className="pt-1.5">{provenanceRow}</div>
      {reliability && (
        <div className="col-start-1 col-span-3 -mt-1">
          <ClauseReliabilityBadge code={def.code} reliability={reliability} review={review} />
        </div>
      )}
      {checks.length > 0 && (
        <div className="col-start-1 col-span-3 flex flex-wrap items-center gap-2" data-testid={`clause-checks-${testId}`}>
          <Badge variant="destructive" className="text-[10px] font-normal">{t('clauses.state.to_verify')}</Badge>
          {checks.map(c => (
            <span key={c.code} className="text-xs text-destructive" data-testid={`clause-check-${c.code}`}>
              {checkReason(c)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// RevertToInheritedButton deletes an amendment's own value of a clause, which
// brings back the value inherited from the contract it amends.
function RevertToInheritedButton({ org, contractUid, def, from }: { org: string; contractUid: string; def: PropertyDefinition; from: string }) {
  const { t } = useTranslation('contracts')
  const del = useDeleteContractPropertyValue(org, contractUid)
  const label = t('clauses.revert_to_inherited', { name: from })
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 px-1.5 text-xs gap-1"
      disabled={del.isPending}
      onClick={() => del.mutate(def.uid)}
      data-testid={`clause-revert-${def.code}`}
      aria-label={label}
      title={label}
    >
      <AppIcon path={mdiUndoVariant} size="xs" />
      {t('clauses.revert')}
    </Button>
  )
}

// normalizeDraft coerces the in-progress draft to the value shape the API expects.
function normalizeDraft(draft: any): unknown {
  return draft
}

interface ClauseEditorProps {
  def: PropertyDefinition
  draft: any
  setDraft: (v: any) => void
  enumValues: { value: string; name: Record<string, string> }[]
  locale: string
  subtype: JsonSubtype
  isMultiple: boolean
  onEnter: () => void
  testId: string
}

function ClauseEditor({ def, draft, setDraft, enumValues, locale, subtype, isMultiple, onEnter, testId }: ClauseEditorProps) {
  const { t } = useTranslation('contracts')
  const inputCls = 'w-full px-2 py-1.5 border border-border rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground'

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onEnter()
    }
  }

  // Multivalued text → compact comma-separated chips input.
  if (isMultiple && (def.data_type === 'text' || def.data_type === 'enum')) {
    const arr: string[] = Array.isArray(draft) ? draft : []
    return (
      <Input
        autoFocus
        defaultValue={arr.join(', ')}
        onChange={(e) => setDraft(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
        onKeyDown={onKey}
        placeholder={t('clauses.multi_placeholder')}
        className={inputCls}
        data-testid={`input-${testId}`}
      />
    )
  }

  switch (def.data_type) {
    case 'boolean':
      return (
        <select
          autoFocus
          value={draft === true ? 'true' : draft === false ? 'false' : ''}
          onChange={(e) => setDraft(e.target.value === '' ? null : e.target.value === 'true')}
          className={inputCls}
          data-testid={`input-${testId}`}
        >
          <option value="">{t('clauses.empty_value')}</option>
          <option value="true">{t('clauses.yes')}</option>
          <option value="false">{t('clauses.no')}</option>
        </select>
      )

    case 'number':
      return (
        <Input
          autoFocus
          type="number"
          value={draft ?? ''}
          onChange={(e) => setDraft(e.target.value === '' ? null : Number(e.target.value))}
          onKeyDown={onKey}
          className={inputCls}
          data-testid={`input-${testId}`}
        />
      )

    case 'enum':
      return (
        <select
          autoFocus
          value={typeof draft === 'string' ? draft : ''}
          onChange={(e) => setDraft(e.target.value || null)}
          className={inputCls}
          data-testid={`input-${testId}`}
        >
          <option value="">{t('clauses.empty_value')}</option>
          {enumValues.map(ev => (
            <option key={ev.value} value={ev.value}>
              {localized(ev.name, locale, ev.value)}
            </option>
          ))}
        </select>
      )

    case 'json':
      if (subtype === 'duration') {
        const dv = (draft && typeof draft === 'object') ? draft : {}
        return (
          <div className="flex items-center gap-1">
            <Input
              autoFocus
              type="number"
              value={dv.value ?? ''}
              onChange={(e) => setDraft({ ...dv, value: e.target.value === '' ? null : Number(e.target.value) })}
              onKeyDown={onKey}
              className={`${inputCls} w-20`}
              data-testid={`input-${testId}-value`}
            />
            <select
              value={dv.unit ?? 'MONTH'}
              onChange={(e) => setDraft({ ...dv, unit: e.target.value })}
              className={`${inputCls} w-28`}
              data-testid={`input-${testId}-unit`}
            >
              {DURATION_UNITS.map(u => (
                <option key={u} value={u}>{t(`clauses.unit.${u}` as any, { defaultValue: u })}</option>
              ))}
            </select>
          </div>
        )
      }
      if (subtype === 'amount') {
        const av = (draft && typeof draft === 'object') ? draft : {}
        return (
          <div className="flex flex-wrap items-center gap-1">
            <Input
              autoFocus
              type="number"
              value={av.amount ?? ''}
              onChange={(e) => setDraft({ ...av, amount: e.target.value === '' ? null : Number(e.target.value) })}
              onKeyDown={onKey}
              placeholder={t('clauses.amount')}
              className={`${inputCls} w-28`}
              data-testid={`input-${testId}-amount`}
            />
            <Input
              value={av.currency ?? 'EUR'}
              onChange={(e) => setDraft({ ...av, currency: e.target.value.toUpperCase() })}
              onKeyDown={onKey}
              className={`${inputCls} w-20`}
              data-testid={`input-${testId}-currency`}
            />
            <select
              value={av.tax_basis ?? 'excluding_tax'}
              onChange={(e) => setDraft({ ...av, tax_basis: e.target.value })}
              className={`${inputCls} w-36`}
              data-testid={`input-${testId}-tax`}
            >
              {TAX_BASES.map(b => (
                <option key={b} value={b}>{t(`clauses.tax_basis.${b}` as any, { defaultValue: b })}</option>
              ))}
            </select>
          </div>
        )
      }
      // Generic JSON — raw text; parsed on save when valid JSON, else stored as string.
      return (
        <Input
          autoFocus
          defaultValue={draft == null ? '' : (typeof draft === 'string' ? draft : JSON.stringify(draft))}
          onChange={(e) => {
            const raw = e.target.value
            try { setDraft(JSON.parse(raw)) } catch { setDraft(raw) }
          }}
          onKeyDown={onKey}
          className={inputCls}
          data-testid={`input-${testId}`}
        />
      )

    default: // text
      return (
        <Input
          autoFocus
          value={typeof draft === 'string' ? draft : (draft ?? '')}
          onChange={(e) => setDraft(e.target.value === '' ? null : e.target.value)}
          onKeyDown={onKey}
          className={inputCls}
          data-testid={`input-${testId}`}
        />
      )
  }
}

// renderDisplay produces the read-only representation of a clause value.
function renderDisplay(opts: {
  def: PropertyDefinition
  current?: ContractPropertyValue
  enumValues: { value: string; name: Record<string, string> }[]
  locale: string
  subtype: JsonSubtype
  isMultiple: boolean
  t: (k: any, o?: any) => string
}): React.ReactNode {
  const { def, current, enumValues, locale, subtype, isMultiple, t } = opts
  const value = current?.value
  if (value === null || value === undefined || value === '') return null

  if (isMultiple && Array.isArray(value)) {
    if (value.length === 0) return null
    return (
      <span className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <Badge key={i} variant="secondary" className="text-xs font-normal">{String(v)}</Badge>
        ))}
      </span>
    )
  }

  switch (def.data_type) {
    case 'boolean':
      return value ? t('clauses.yes') : t('clauses.no')
    case 'enum': {
      const match = enumValues.find(ev => ev.value === value)
      return match ? localized(match.name, locale, String(value)) : String(value)
    }
    case 'json': {
      const v = value as any
      if (subtype === 'duration' && v && typeof v === 'object') {
        return `${v.value ?? ''} ${t(`clauses.unit.${v.unit}` as any, { defaultValue: v.unit ?? '' })}`.trim()
      }
      if (subtype === 'amount' && v && typeof v === 'object') {
        const basis = v.tax_basis ? ` (${t(`clauses.tax_basis.${v.tax_basis}` as any, { defaultValue: v.tax_basis })})` : ''
        return `${v.amount ?? ''} ${v.currency ?? ''}${basis}`.trim()
      }
      return typeof v === 'string' ? v : JSON.stringify(v)
    }
    default:
      return String(value)
  }
}
